import { V3Pill } from "@/components/OpsSectionHead";
import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, DollarSign, Users, Calendar, ArrowUp, ArrowDown,
  RefreshCw, Dog, Loader2, Layers, CreditCard, Banknote, CheckSquare,
  Clock, AlertCircle, Info
} from "lucide-react";
import {
  BarChart, Bar, ComposedChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Tooltip as UITip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ExecKpiRow2, PaceLine, DogsTodayCard } from "../components/exec/ExecKpis";
import { MondayBriefing, StaffingPlanner } from "../components/exec/ExecBriefing";
import { DaycareByWeekday, ExecPhase2, Pill } from "../components/exec/ExecPhase2";
import RevenueMix from "../components/RevenueMix";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Formatting ──────────────────────────────────────────────────────────
const fmtFull = (n) =>
  n == null ? "$0" : `$${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const ChangeIndicator = ({ value, size = "sm" }) => {
  if (value == null || value === 0) return null;
  const positive = value > 0;
  const Icon = positive ? ArrowUp : ArrowDown;
  const color = positive ? "text-emerald-600" : "text-red-500";
  const textSize = size === "lg" ? "text-base font-bold" : "text-xs font-semibold";
  return (
    <span className={`inline-flex items-center gap-0.5 ${color} ${textSize}`} data-testid="change-indicator">
      <Icon className={size === "lg" ? "h-4 w-4" : "h-3 w-3"} />
      {Math.abs(value)}%
    </span>
  );
};

// ── KPI Card ────────────────────────────────────────────────────────────
const CASH_TOOLTIP = "Cash actually collected (credit card, cash, check) on the day it was received, minus refunds, excluding store credit. The \"Closed thru\" figure reconciles to Gingr's \"Revenue by Date\" report to the penny. Today is shown separately (+ Today) because it's still live and moving during business hours; closed + today = total.";

const KPICard = ({ title, value, subtitle, change, icon: Icon, color = "blue", tooltip, footer }) => {
  const colorMap = {
    blue: "from-blue-600 to-blue-700",
    emerald: "from-emerald-600 to-emerald-700",
    amber: "from-amber-500 to-amber-600",
    indigo: "from-indigo-600 to-indigo-700",
  };
  return (
    <Card className="overflow-hidden border-0 shadow-md" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1">
            {title}
            {tooltip && (
              <TooltipProvider delayDuration={100}>
                <UITip>
                  <TooltipTrigger asChild>
                    <span data-testid="cash-reconcile-tooltip" className="cursor-help inline-flex">
                      <Info className="h-3 w-3 text-gray-400" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs leading-relaxed">
                    <p>{tooltip}</p>
                  </TooltipContent>
                </UITip>
              </TooltipProvider>
            )}
          </span>
          {Icon && <Icon className="h-4 w-4 text-gray-400" />}
        </div>
        <div className="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight">{value}</div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
          {change != null && <ChangeIndicator value={change} />}
        </div>
        {footer}
      </CardContent>
    </Card>
  );
};

// ── Section Header ──────────────────────────────────────────────────────
const SectionHeader = ({ title, children }) => (
  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
    <h2 className="m-0"><V3Pill>{title}</V3Pill></h2>
    {children && <div className="flex items-center gap-2">{children}</div>}
  </div>
);

// ── Custom Tooltip ──────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="flex justify-between gap-4">
          <span>{entry.name}:</span>
          <span className="font-bold">
            {typeof entry.value === "number"
              ? (/rev|cash/.test(entry.name?.toLowerCase() || "")
                  ? fmtFull(entry.value)
                  : entry.value.toLocaleString("en-US"))
              : entry.value}
          </span>
        </p>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export default function ExecDashboard() {
  const [data, setData] = useState(null);
  const [monthly12m, setMonthly12m] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [opsFilter, setOpsFilter] = useState("boarding");
  const [metric, setMetric] = useState("revenue"); // revenue | counts
  const [range, setRange] = useState("12m");        // 12m | daily
  const [dogCount12m, setDogCount12m] = useState(null);
  const [dogCountView, setDogCountView] = useState("total"); // total | avg
  const staleRefreshTimer = useRef(null);
  const cashPollCount = useRef(0);
  // Org-view detection: show "Org View" button iff user's tenant has 2+ locations
  const [hasOrgView, setHasOrgView] = useState(false);

  // ── Cash Revenue mode ─────────────────────────────────────────────────
  // Default to "cash" (cash-accounting view). Persisted in localStorage.
  const [revenueMode, setRevenueModeState] = useState(
    () => localStorage.getItem("exec_revenue_mode") || "cash"
  );
  const [cashData, setCashData] = useState(null);
  const [cashLoading, setCashLoading] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);

  const setRevenueMode = (mode) => {
    setRevenueModeState(mode);
    localStorage.setItem("exec_revenue_mode", mode);
  };

  useEffect(() => {
    // Show the Org View button ONLY when the user's CURRENT tenant has 2+
    // locations. Applies to every role — super_admins viewing a single-loc
    // tenant don't need the org-rollup button on this page (they can reach
    // any tenant's org view from the Admin → Tenants panel).
    const cachedUser = JSON.parse(localStorage.getItem("cachedUser") || "{}");
    if (!cachedUser.tenant_id) return;
    axios.get(`${API}/tenants/me`, { timeout: 10000 })
      .then(res => {
        if ((res.data?.location_count ?? 0) >= 2) setHasOrgView(true);
      })
      .catch(() => {});
  }, []);

  const fetchData = async (opts = {}) => {
    const { showRefresh = false, isStaleRefresh = false } = opts;
    if (showRefresh || isStaleRefresh) setRefreshing(true);
    else if (!data) setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/ops/exec-dashboard`, { timeout: 120000 });
      setData(res.data);
      // 12M trend (non-blocking — fetch in background)
      axios.get(`${API}/ops/monthly-revenue-trend?months=12`, { timeout: 240000 })
        .then(r => setMonthly12m(r.data))
        .catch(() => {});

      // 12M dog-count history (non-blocking — heavy Gingr scan, so long timeout)
      axios.get(`${API}/ops/dog-count-trend?months=12`, { timeout: 240000 })
        .then(r => setDogCount12m(r.data))
        .catch(() => {});

      // SWR: if payload was served stale, poll once after a delay for the fresh refresh
      if (res.data?._cache?.is_stale && !isStaleRefresh) {
        if (staleRefreshTimer.current) clearTimeout(staleRefreshTimer.current);
        staleRefreshTimer.current = setTimeout(() => {
          fetchData({ isStaleRefresh: true });
        }, 15000);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Access denied. This dashboard is restricted to Admin, Manager, and Super Admin roles.");
      } else if (!data) {
        // Only surface error if we have nothing to show
        setError("Failed to load executive dashboard. Please try again.");
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchCashData = async (silent = false) => {
    if (!silent) setCashLoading(true);
    try {
      const [kpisRes, statusRes] = await Promise.allSettled([
        axios.get(`${API}/cash-revenue/kpis`, { timeout: 30000 }),
        axios.get(`${API}/cash-revenue/backfill/status`, { timeout: 10000 }),
      ]);
      if (kpisRes.status === "fulfilled") {
        setCashData(kpisRes.value.data);
        // "Today" computes in the background server-side. Poll quietly until it lands
        // (cap at ~8 polls / ~40s) so MTD/YTD show instantly and Today fills in.
        if (kpisRes.value.data?.today_computing && cashPollCount.current < 8) {
          cashPollCount.current += 1;
          if (staleRefreshTimer.current) clearTimeout(staleRefreshTimer.current);
          staleRefreshTimer.current = setTimeout(() => fetchCashData(true), 5000);
        } else {
          cashPollCount.current = 0;
        }
      }
      if (statusRes.status === "fulfilled") setBackfillStatus(statusRes.value.data);
    } catch {
      // Silently fail — fall back to invoice mode
    } finally {
      if (!silent) setCashLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCashData();
    return () => {
      if (staleRefreshTimer.current) clearTimeout(staleRefreshTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Daily trend chart data ──────────────────────────────────────────
  const trendData = useMemo(() => {
    if (!data?.charts?.daily_trend) return [];
    return data.charts.daily_trend.map((d) => ({
      date: new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      Boarding: d.boarding,
      Daycare: d.daycare,
      Total: d.total,
      "PY Total": d.py_total,
      "Close Rev": d.revenue,
      "PY Rev": d.py_revenue,
    }));
  }, [data]);

  // ── Service drilldown chart data (legacy — replaced by RevenueMix) ────
  // Removed

  // ── 12M monthly trend chart data ──────────────────────────────────────
  const monthly12mData = useMemo(() => {
    if (!monthly12m?.months) return [];
    return monthly12m.months.map((m) => ({
      label: m.label,
      "Current Rev": m.revenue || 0,
      "PY Rev": m.py_revenue || 0,
    }));
  }, [monthly12m]);

  // ── 12M dog-count history chart data (Boarding vs Daycare) ────────────
  const dogCount12mData = useMemo(() => {
    if (!dogCount12m?.months) return [];
    const avg = dogCountView === "avg";
    return dogCount12m.months.map((m) => ({
      label: m.label,
      Boarding: avg ? (m.boarding_avg || 0) : (m.boarding || 0),
      Daycare: avg ? (m.daycare_avg || 0) : (m.daycare || 0),
    }));
  }, [dogCount12m, dogCountView]);

  // ── Daily CASH trend (last 31 days) — for Revenue+Daily in cash mode ──
  const cashDailyData = useMemo(() => {
    if (!cashData?.daily_trend) return [];
    return cashData.daily_trend.map((d) => ({
      date: d.label,
      "Cash Rev": d.curr || 0,
      "PY Rev": d.py || 0,
    }));
  }, [cashData]);

  // ── Cash 12M trend chart data (for cash mode 12M chart) ─────────────
  const cashTrendData = useMemo(() => {
    if (!cashData?.monthly_trend) return [];
    return cashData.monthly_trend.map((m) => ({
      label: m.label,
      "Cash Rev": m.curr || 0,
      "PY Rev": m.py || 0,
    }));
  }, [cashData]);

  // ── Forward ops chart data (stacked) ──────────────────────────────────
  const opsData = useMemo(() => {
    if (!data?.charts?.forward_ops) return [];
    return data.charts.forward_ops.map((w) => ({
      week: w.label,
      Boarding: w.boarding,
      Daycare: w.daycare,
      "Check Ins": w.check_ins,
      "Check Outs": w.check_outs,
    }));
  }, [data]);

  // ── Loading / Error ───────────────────────────────────────────────────
  // Pick revenue source based on toggle — must be before early returns (useMemo depends on cr)
  const isCashMode = revenueMode === "cash";
  const cr = isCashMode && cashData ? cashData : null;

  // "Last updated X min ago" for today's cash — MUST be before any early returns
  const cashUpdatedAgo = useMemo(() => {
    if (!cr?.today_last_updated) return null;
    try {
      const secs = (Date.now() - new Date(cr.today_last_updated).getTime()) / 1000;
      if (secs < 90) return "just now";
      if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
      return `${Math.round(secs / 3600)}h ago`;
    } catch { return null; }
  }, [cr]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4" data-testid="exec-dash-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-gray-500 font-medium">Loading Executive Dashboard...</p>
        <p className="text-xs text-gray-400">Fetching revenue & operations data from Gingr</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4" data-testid="exec-dash-error">
        <div className="text-red-500 text-lg font-semibold">{error}</div>
        <Button onClick={() => fetchData()} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  const rev = data?.kpis?.revenue || {};
  const occ = data?.kpis?.occupancy?.current || {};
  const occPy = data?.kpis?.occupancy?.prior_year || {};
  const leads = data?.leads || {};

  // Pick revenue source based on toggle
  const revToday = cr ? cr.today : (rev.today ?? 0);
  const revTodayPY = cr ? cr.today_py : (rev.today_py ?? 0);
  const revTodayChange = cr ? cr.today_change : (rev.today_change ?? null);
  const revMTD = cr ? cr.mtd : (rev.mtd ?? 0);
  const revMTDPY = cr ? cr.mtd_py : (rev.mtd_py ?? 0);
  const revMTDChange = cr ? cr.mtd_change : (rev.mtd_change ?? null);
  const revYTD = cr ? cr.ytd : (rev.ytd ?? 0);
  const revYTDPY = cr ? cr.ytd_py : (rev.ytd_py ?? 0);
  const revYTDChange = cr ? cr.ytd_change : (rev.ytd_change ?? null);
  // Cash mode "closed vs today" split — closed days tie to Gingr; today is live.
  const revMTDClosed = cr ? cr.mtd_closed : null;
  const revMTDToday = cr ? cr.mtd_today : null;
  const revYTDClosed = cr ? cr.ytd_closed : null;
  const revYTDToday = cr ? cr.ytd_today : null;
  const closedThrough = cr ? cr.closed_through : null;
  const fmtMD = (iso) => { if (!iso) return ""; const p = iso.split("-"); return `${parseInt(p[1], 10)}/${parseInt(p[2], 10)}`; };
  const closedTodayFooter = (todayVal, totalVal, testid) => (
    <div className="mt-2 pt-2 border-t border-gray-100 space-y-1" data-testid={testid}>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>+ Today (live)</span>
        <span className="font-medium text-gray-700">{fmtFull(todayVal)}</span>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-gray-600">= Total</span>
        <span className="font-bold text-gray-900" data-testid={`${testid}-total`}>{fmtFull(totalVal)}</span>
      </div>
    </div>
  );

  // Pace: so far + last year's remaining × growth (MTD: last 3 full months; YTD: year-to-date), keeps seasonality.
  const _now = new Date();
  const _trend = isCashMode ? (cashData?.monthly_trend || []) : (monthly12m?.months || []);
  const _cur = (m) => (isCashMode ? m.curr : m.revenue) || 0;
  const _py = (m) => (isCashMode ? m.py : m.py_revenue) || 0;
  const _n = _trend.length;
  const _lyFull = _n ? _py(_trend[_n - 1]) : null;
  const _last3 = _n >= 4 ? _trend.slice(_n - 4, _n - 1) : [];
  const _g3 = _last3.reduce((a, m) => a + _py(m), 0) ? _last3.reduce((a, m) => a + _cur(m), 0) / _last3.reduce((a, m) => a + _py(m), 0) : null;
  const _monthName = _now.toLocaleString("en-US", { month: "long" });
  const mtdPace = revMTD && _lyFull && _g3 ? {
    kind: "mtd", soFar: revMTD, lyRest: Math.max(0, _lyFull - revMTDPY), growth: Math.round(_g3 * 100) / 100, monthName: _monthName, lyFull: _lyFull,
    growthLabel: _last3.map((m) => m.label).join(", "),
  } : null;
  if (mtdPace) { mtdPace.value = mtdPace.soFar + mtdPace.lyRest * mtdPace.growth; mtdPace.vsLy = Math.round((mtdPace.value / _lyFull - 1) * 100); }
  // LY months after this month (e.g. Nov–Dec last year) sit at the start of the trailing-12 trend as "current".
  const _lyLater = _n === 12 ? _trend.slice(0, 11 - _now.getMonth()) : [];
  const _gY = revYTDPY ? revYTD / revYTDPY : null;
  const ytdPace = revYTD && _gY && _lyFull && _n === 12 ? {
    kind: "ytd", soFar: revYTD, growth: Math.round(_gY * 100) / 100, lyRest: Math.max(0, _lyFull - revMTDPY) + _lyLater.reduce((a, m) => a + _cur(m), 0),
    restLabel: `rest of ${_monthName.slice(0, 3)}${_lyLater.length ? `, ${_lyLater.map((m) => m.label.split(" ")[0]).join(", ")}` : ""}`,
  } : null;
  if (ytdPace) { ytdPace.value = ytdPace.soFar + ytdPace.lyRest * ytdPace.growth; ytdPace.lyFull = revYTDPY + ytdPace.lyRest; ytdPace.vsLy = Math.round((ytdPace.value / ytdPace.lyFull - 1) * 100); }

  // Backfill trigger
  const triggerBackfill = async () => {
    try {
      await axios.post(`${API}/cash-revenue/backfill/start`, {});
      toast.success("Historical backfill started — runs in background (~30-45 min)");
      setTimeout(() => fetchCashData(), 3000);
    } catch { toast.error("Could not start backfill"); }
  };

  // Resume a backfill whose background task died (stalled). Idempotent — already-computed
  // days are skipped, so only the remaining days are processed.
  const resumeBackfill = async () => {
    try {
      await axios.post(`${API}/cash-revenue/backfill/start`, {});
      toast.success("Resuming backfill — remaining days are computing in the background");
      setBackfillStatus((s) => (s ? { ...s, status: "running" } : s));
      setTimeout(() => fetchCashData(), 3000);
    } catch { toast.error("Could not resume backfill"); }
  };

  return (
    <div className="ops-v3 space-y-6" data-testid="exec-dashboard">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight"
            style={{ fontFamily: "Manrope, sans-serif" }}>
            Executive Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Real-time revenue & operations overview</p>
        </div>
        <div className="flex items-center gap-2">
          {refreshing && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold text-[10px]"
              data-testid="exec-dash-refreshing-badge"
              title="Refreshing latest data in the background">
              <Loader2 className="h-2.5 w-2.5 animate-spin" />
              Refreshing
            </span>
          )}
          {data?.generated_at && (
            <span className="text-xs text-gray-400">
              Updated {new Date(data.generated_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <Button size="sm" variant="outline" onClick={() => { fetchData({ showRefresh: true }); window.dispatchEvent(new Event("exec:refresh")); }} disabled={refreshing}
            data-testid="exec-dash-refresh"
            title="Refresh data"
            aria-label="Refresh data"
            className="h-9 w-9 p-0">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
          {hasOrgView && (
            <Link to="/exec-dashboard/tenant">
              <Button
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="org-view-btn"
              >
                <Layers className="h-3.5 w-3.5 mr-1.5" /> Org View
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* ── Revenue mode toggle ──────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1" data-testid="revenue-mode-toggle">
          <button
            onClick={() => setRevenueMode("cash")}
            data-testid="revenue-mode-cash"
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
              isCashMode
                ? "bg-[#1F8A7C] text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Cash Collected
          </button>
          <button
            onClick={() => setRevenueMode("invoice")}
            data-testid="revenue-mode-invoice"
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
              !isCashMode
                ? "bg-[#1F8A7C] text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Closed Invoice
          </button>
        </div>
        {/* Backfill status / trigger */}
        {isCashMode && cashData?.backfill_needed && backfillStatus?.status !== "running" && backfillStatus?.status !== "complete" && (
          <button
            onClick={triggerBackfill}
            data-testid="backfill-trigger-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-all"
          >
            <AlertCircle className="h-3.5 w-3.5" />
            Run Historical Backfill ({cashData.coverage_pct}% coverage)
          </button>
        )}
        {isCashMode && backfillStatus?.status === "running" && (
          <span className="flex items-center gap-1.5 text-xs text-amber-700 font-semibold">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Backfill running… {backfillStatus.progress_pct ?? 0}% ({backfillStatus.days_processed}/{backfillStatus.days_total} days)
          </span>
        )}
        {isCashMode && backfillStatus?.status === "stalled" && (
          <button
            onClick={resumeBackfill}
            data-testid="backfill-resume-btn"
            title="The previous backfill task stopped (likely a server restart). Resume to finish the remaining days."
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-all"
          >
            <AlertCircle className="h-3.5 w-3.5" />
            Backfill stalled at {backfillStatus.progress_pct ?? 0}% — Resume
          </button>
        )}
        {isCashMode && cashUpdatedAgo && (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Today updated {cashUpdatedAgo}
          </span>
        )}
      </div>

      {/* ── ROW 1: KPI Cards ─────────────────────────────────────────── */}
      <div data-testid="exec-section-glance"><Pill>Today at a Glance</Pill></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {/* Today Revenue — cash mode shows payment breakdown */}
        {isCashMode && cashLoading ? (
          <Card className="overflow-hidden border-0 shadow-md lg:col-span-1" data-testid="kpi-today-cash-loading">
            <CardContent className="p-4 flex items-center justify-center min-h-[120px]">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
            </CardContent>
          </Card>
        ) : isCashMode && cashData ? (
          <Card className="overflow-hidden border-0 shadow-md lg:col-span-1" data-testid="kpi-today-cash">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                  Today's Cash
                  <TooltipProvider delayDuration={100}>
                    <UITip>
                      <TooltipTrigger asChild>
                        <span data-testid="cash-reconcile-tooltip" className="cursor-help inline-flex">
                          <Info className="h-3 w-3 text-gray-400" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs leading-relaxed">
                        <p>{CASH_TOOLTIP}</p>
                      </TooltipContent>
                    </UITip>
                  </TooltipProvider>
                </span>
                <DollarSign className="h-4 w-4 text-gray-400" />
              </div>
              <div className="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
                {fmtFull(revToday)}
                {cashData.today_computing && (
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-500" data-testid="today-cash-computing" />
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                {revTodayPY > 0 && <span className="text-xs text-gray-500">PY: {fmtFull(revTodayPY)}</span>}
                <ChangeIndicator value={revTodayChange} />
              </div>
              {/* Payment method breakdown */}
              <div className="mt-2.5 pt-2 border-t border-gray-100 grid grid-cols-3 gap-1 text-center">
                {[
                  { label: "Credit", val: cashData.today_credit_card, Icon: CreditCard, color: "text-blue-600" },
                  { label: "Cash", val: cashData.today_cash, Icon: Banknote, color: "text-emerald-600" },
                  { label: "Check", val: cashData.today_check, Icon: CheckSquare, color: "text-amber-600" },
                ].map(({ label, val, Icon, color }) => (
                  <div key={label} className="flex flex-col items-center">
                    <Icon className={`h-3 w-3 ${color} mb-0.5`} />
                    <span className={`text-xs font-bold ${color}`}>{fmtFull(val)}</span>
                    <span className="text-[10px] text-gray-400">{label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : isCashMode && !cashData ? (
          <Card className="overflow-hidden border-0 shadow-md lg:col-span-1" data-testid="kpi-today-cash-unavailable">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Today's Cash</span>
                <AlertCircle className="h-4 w-4 text-amber-400" />
              </div>
              <div className="text-sm text-amber-700 font-medium mt-2">Cash data unavailable</div>
              <div className="text-xs text-gray-400 mt-1">Check Gingr integration settings</div>
            </CardContent>
          </Card>
        ) : (
          <KPICard
            title="Current Day Revenue"
            value={fmtFull(revToday)}
            subtitle={revTodayPY > 0 ? `PY: ${fmtFull(revTodayPY)}` : "Closed Invoice Revenue"}
            change={revTodayChange}
            icon={DollarSign}
            color="amber"
          />
        )}
        <KPICard
          title={isCashMode ? "Cash MTD" : "Month-to-Date Revenue"}
          value={fmtFull(isCashMode ? revMTDClosed : revMTD)}
          subtitle={isCashMode ? `Closed thru ${fmtMD(closedThrough)} · ties to Gingr` : `${fmtFull(revMTDPY)} PY MTD`}
          change={revMTDChange}
          icon={Calendar}
          color="blue"
          tooltip={isCashMode ? CASH_TOOLTIP : undefined}
          data-testid={isCashMode ? "kpi-cash-mtd" : "kpi-invoice-mtd"}
          footer={<>{isCashMode && closedTodayFooter(revMTDToday, revMTD, "cash-mtd-breakdown")}<PaceLine pace={mtdPace} label="this month" tid="kpi-mtd-pace" /></>}
        />
        <KPICard
          title={isCashMode ? "Cash YTD" : "Year-to-Date Revenue"}
          value={fmtFull(isCashMode ? revYTDClosed : revYTD)}
          subtitle={isCashMode ? `Closed thru ${fmtMD(closedThrough)} · ties to Gingr` : `${fmtFull(revYTDPY)} PY YTD`}
          change={revYTDChange}
          icon={TrendingUp}
          color="emerald"
          tooltip={isCashMode ? CASH_TOOLTIP : undefined}
          data-testid={isCashMode ? "kpi-cash-ytd" : "kpi-invoice-ytd"}
          footer={<>{isCashMode && closedTodayFooter(revYTDToday, revYTD, "cash-ytd-breakdown")}<PaceLine pace={ytdPace} label={`in ${new Date().getFullYear()}`} tid="kpi-ytd-pace" /></>}
        />
        <DogsTodayCard occ={occ} change={occPy.total_active ? Math.round(((occ.total_active - occPy.total_active) / occPy.total_active) * 100) : null} />
      </div>

      <ExecKpiRow2 />

      {/* ── ROW 2: Revenue / Dog Count Overview ──────────────────────── */}
      <Card className="border-0 shadow-md overflow-hidden" data-testid="revenue-overview-section">
        <SectionHeader title="Revenue Overview">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {/* Metric selector */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">View</span>
              <div className="inline-flex rounded-lg bg-gray-100 p-0.5" data-testid="overview-metric-toggle">
                <button onClick={() => setMetric("revenue")} data-testid="metric-revenue"
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${metric === "revenue" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-200"}`}>
                  Revenue
                </button>
                <button onClick={() => setMetric("counts")} data-testid="metric-counts"
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${metric === "counts" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-200"}`}>
                  Dog Counts
                </button>
              </div>
            </div>
            {/* Time-range selector */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Period</span>
              <div className="inline-flex rounded-lg bg-gray-100 p-0.5" data-testid="overview-range-toggle">
                <button onClick={() => setRange("12m")} data-testid="range-12m"
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${range === "12m" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-200"}`}>
                  12 Months
                </button>
                <button onClick={() => setRange("daily")} data-testid="range-daily"
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${range === "daily" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-200"}`}>
                  Daily (31d)
                </button>
              </div>
            </div>
            {/* Total / Avg sub-toggle — only for 12-month dog counts */}
            {metric === "counts" && range === "12m" && (
              <div className="inline-flex rounded-lg bg-gray-100 p-0.5" data-testid="dog-count-view-toggle">
                <button onClick={() => setDogCountView("total")} data-testid="dog-count-view-total"
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${dogCountView === "total" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-200"}`}>
                  Total
                </button>
                <button onClick={() => setDogCountView("avg")} data-testid="dog-count-view-avg"
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${dogCountView === "avg" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-200"}`}>
                  Avg / Day
                </button>
              </div>
            )}
          </div>
        </SectionHeader>
        <CardContent className="p-4 pt-0">
          <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700" data-testid="overview-chart-title">
                {metric === "counts"
                  ? (range === "daily"
                      ? "Daily Dog Counts — Last 31 Days"
                      : `Monthly Dog Counts — Trailing 12 Months ${dogCountView === "avg" ? "(avg dogs/day)" : "(total dog-days)"}`)
                  : (range === "daily"
                      ? (isCashMode ? "Daily Cash Collected — Last 31 Days" : "Daily Close Invoice Revenue — Last 31 Days")
                      : isCashMode
                        ? "Monthly Cash Collected — Trailing 12 Months"
                        : "Monthly Close Invoice Revenue — Trailing 12 Months")}
              </h3>
              <div className="flex items-center gap-3 text-xs">
                {metric === "counts" ? (
                  <>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-600 inline-block" />Boarding</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" />Daycare</span>
                    {range === "daily" && (
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-300 inline-block" />PY Total</span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-600 inline-block" />Current</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-300 inline-block" />Prior Year</span>
                  </>
                )}
              </div>
            </div>
            {metric === "counts" && range === "12m" && !dogCount12m ? (
              <div className="flex items-center justify-center text-sm text-gray-400" style={{ height: 260 }} data-testid="dog-count-loading">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading dog-count history…
              </div>
            ) : (
            <ResponsiveContainer width="100%" height={260}>
              {metric === "counts" && range === "daily" ? (
                <ComposedChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b7280" }} interval={4} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} width={35} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="Boarding" name="Boarding" fill="#2563eb" radius={[2, 2, 0, 0]} stackId="dogs" barSize={16} />
                  <Bar dataKey="Daycare" name="Daycare" fill="#10b981" radius={[2, 2, 0, 0]} stackId="dogs" barSize={16} />
                  <Line dataKey="PY Total" name="PY Total" stroke="#d1d5db" strokeWidth={2} dot={false} type="monotone" />
                </ComposedChart>
              ) : metric === "counts" ? (
                <ComposedChart data={dogCount12mData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} width={40} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="Boarding" name="Boarding" fill="#2563eb" radius={[2, 2, 0, 0]} stackId="dogs" barSize={22} />
                  <Bar dataKey="Daycare" name="Daycare" fill="#10b981" radius={[2, 2, 0, 0]} stackId="dogs" barSize={22} />
                </ComposedChart>
              ) : range === "12m" && isCashMode ? (
                <ComposedChart data={cashTrendData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} width={50} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="Cash Rev" name="Cash Collected" fill="#10b981" radius={[2, 2, 0, 0]} barSize={28} />
                  <Line dataKey="PY Rev" name="PY Cash" stroke="#d1d5db" strokeWidth={2} dot={{ r: 3 }} type="monotone" />
                </ComposedChart>
              ) : range === "12m" ? (
                <ComposedChart data={monthly12mData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} width={50} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="Current Rev" name="Current Revenue" fill="#2563eb" radius={[2, 2, 0, 0]} barSize={28} />
                  <Line dataKey="PY Rev" name="PY Revenue" stroke="#d1d5db" strokeWidth={2} dot={{ r: 3 }} type="monotone" />
                </ComposedChart>
              ) : range === "daily" && isCashMode ? (
                <ComposedChart data={cashDailyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b7280" }} interval={4} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} width={45} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="Cash Rev" name="Cash Collected" fill="#10b981" radius={[2, 2, 0, 0]} barSize={16} />
                  <Line dataKey="PY Rev" name="PY Cash" stroke="#d1d5db" strokeWidth={2} dot={false} type="monotone" />
                </ComposedChart>
              ) : (
                <ComposedChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b7280" }} interval={4} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} width={45} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="Close Rev" name="Close Invoice Revenue" fill="#2563eb" radius={[2, 2, 0, 0]} barSize={16} />
                  <Line dataKey="PY Rev" name="PY Revenue" stroke="#d1d5db" strokeWidth={2} dot={false} type="monotone" />
                </ComposedChart>
              )}
            </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── ROW 3: Revenue Mix (full width) ───────────────────────── */}
      <RevenueMix />
      <MondayBriefing />

      <DaycareByWeekday />
      <ExecPhase2 />


      {/* ── ROW 4: Leads Overview + Day-to-Day Ops (side-by-side on lg+, stacked on mobile) ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Leads Overview */}
        <Card className="border-0 shadow-md overflow-hidden" data-testid="leads-overview-section">
          <SectionHeader title="Leads Overview">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-semibold text-[10px] uppercase tracking-wider"
              title="All-time totals across every lead for this location. The Leads Dashboard KPIs are scoped to a selected date range, so numbers there will differ by timeframe."
              data-testid="leads-overview-timeframe"
            >
              All-time
            </span>
          </SectionHeader>
          <CardContent className="p-4 pt-0">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <div className="text-xs text-blue-600 font-semibold">Total Leads</div>
                <div className="text-2xl font-extrabold text-gray-900">{leads.total_leads || 0}</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <div className="text-xs text-emerald-600 font-semibold">Tours Scheduled</div>
                <div className="text-2xl font-extrabold text-gray-900">{leads.tours || 0}</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <div className="text-xs text-amber-600 font-semibold">Converted</div>
                <div className="text-2xl font-extrabold text-gray-900">{leads.converted || 0}</div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 mb-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 font-medium">Conversion Rate</span>
                <span className="text-xl font-extrabold text-gray-900">{leads.conversion_rate || 0}%</span>
              </div>
            </div>

            {/* Lead Funnel */}
            <div className="text-xs text-gray-500 font-medium mb-2">Lead Pipeline</div>
            <div className="space-y-1.5">
              {[
                { label: "New", value: leads.new_leads || 0, color: "bg-blue-500" },
                { label: "Contacted", value: leads.contacted || 0, color: "bg-cyan-500" },
                { label: "Qualified", value: leads.qualified || 0, color: "bg-indigo-500" },
                { label: "Converted", value: leads.converted || 0, color: "bg-emerald-500" },
                { label: "Lost", value: leads.lost || 0, color: "bg-red-400" },
              ].map((item) => {
                const maxVal = Math.max(leads.total_leads || 1, 1);
                return (
                  <div key={item.label} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-16 text-right">{item.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                      <div
                        className={`${item.color} h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500`}
                        style={{ width: `${Math.max((item.value / maxVal) * 100, item.value > 0 ? 10 : 0)}%` }}
                      >
                        {item.value > 0 && <span className="text-[10px] font-bold text-white">{item.value}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {leads.lead_revenue > 0 && (
              <div className="mt-3 bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-emerald-600 font-medium">Lead Revenue (Converted)</span>
                  <span className="text-lg font-extrabold text-gray-900">{fmtFull(leads.lead_revenue)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Day-to-Day Operations (stacked bars) */}
        <Card className="border-0 shadow-md overflow-hidden" data-testid="forward-ops-section">
          <SectionHeader title="Day-to-Day Operations — Forward Looking">
            <div className="flex gap-1">
              {["boarding", "daycare"].map((f) => (
                <Button key={f} size="sm"
                  variant={opsFilter === f ? "default" : "outline"}
                  className={`h-7 text-xs capitalize ${opsFilter === f ? "bg-blue-700 text-white" : ""}`}
                  onClick={() => setOpsFilter(f)}
                  data-testid={`ops-filter-${f}`}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
          </SectionHeader>
          <CardContent className="p-4 pt-0">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={opsData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#6b7280" }} />
                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} width={30} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: "11px" }} />
                <Bar dataKey={opsFilter === "boarding" ? "Boarding" : "Daycare"} name="Dogs"
                  fill="#1F8A7C" radius={[0, 0, 0, 0]} stackId="ops" barSize={22} />
                <Bar dataKey="Check Ins" name="Check Ins"
                  fill="#8B8AE0" radius={[0, 0, 0, 0]} stackId="ops" barSize={22} />
                <Bar dataKey="Check Outs" name="Check Outs"
                  fill="#CBD5DC" radius={[2, 2, 0, 0]} stackId="ops" barSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      <StaffingPlanner />
    </div>
  );
}
